/**
 * GaauCharts.js — Resolve os marcadores [GRAFICO_*] dos capítulos 4.2.x (Inventário Florestal)
 * do PMAU, substituindo-os por gráficos Highcharts reais, capturados via `chart.getSVG()` —
 * mesmo padrão usado no Capítulo 3 (ver ParticipatoryDiagnosisCharts.js / SurveyCharts.js).
 *
 * Os rótulos e cores abaixo espelham as constantes de ai-service/app/gaau/analytics.py e
 * ai-service/app/gaau/charts.py — os dois lados precisam ficar em sincronia.
 */

import Highcharts from "highcharts";
import HighchartsExporting from "highcharts/modules/exporting";
import PmauService from "../servico/PmauService";

if (typeof HighchartsExporting === "function") {
  HighchartsExporting(Highcharts);
}

const CHART_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const DAP_CLASS_LABELS = ["0–15 cm", "15–30 cm", "30–45 cm", "45–60 cm", "60–75 cm", ">75 cm"];
const HEIGHT_CLASS_LABELS = ["Pequeno Porte (< 6 m)", "Médio Porte (6–12 m)", "Grande Porte (> 12 m)"];
const ORIGINS = ["Nativa", "Exótica", "Exótica Invasora"];
const ORIGIN_COLORS = {
  Nativa: "#3a8a3a",
  Exótica: "#d97706",
  "Exótica Invasora": "#b91c1c"
};

const GAAU_CATEGORIES = ["Baixo", "Médio", "Alto", "Prioritário"];
const GAAU_KEYS = ["baixo", "medio", "alto", "prioritario"];
const GAAU_COLORS = {
  Baixo: "#22c55e",
  Médio: "#eab308",
  Alto: "#f97316",
  Prioritário: "#ef4444"
};

// ── Highcharts option builders — 4.2.2 Variáveis Dendrométricas ──────────────

export function buildDapChartOptions(stats) {
  return {
    chart: { type: "column", height: 420, backgroundColor: "#ffffff", style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: {
      categories: DAP_CLASS_LABELS,
      labels: { style: { fontSize: "12px", fontFamily: CHART_FONT } }
    },
    yAxis: { title: { text: "Número de indivíduos" }, allowDecimals: false },
    plotOptions: {
      column: { borderRadius: 3, dataLabels: { enabled: true, style: { fontSize: "10px" } } }
    },
    colors: ORIGINS.map((o) => ORIGIN_COLORS[o]),
    series: ORIGINS.map((origin) => ({
      name: origin,
      data: DAP_CLASS_LABELS.map((lbl) => (stats.dap_classes[lbl] || {})[origin] || 0)
    })),
    legend: { enabled: true },
    credits: { enabled: false }
  };
}

export function buildHeightChartOptions(stats) {
  return {
    chart: { type: "column", height: 360, backgroundColor: "#ffffff", style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: {
      categories: HEIGHT_CLASS_LABELS,
      labels: { style: { fontSize: "12px", fontFamily: CHART_FONT } }
    },
    yAxis: { title: { text: "Número de indivíduos" }, allowDecimals: false },
    plotOptions: {
      column: { color: "#3a7db5", borderRadius: 3, dataLabels: { enabled: true, style: { fontSize: "12px", fontWeight: "bold" } } }
    },
    series: [{ name: "Indivíduos", data: HEIGHT_CLASS_LABELS.map((lbl) => stats.height_classes[lbl] || 0) }],
    legend: { enabled: false },
    credits: { enabled: false }
  };
}

// ── Highcharts option builders — 4.2.3 Espécies Encontradas ──────────────────

export function buildSpeciesChartOptions(stats) {
  const items = stats.top_species.slice().reverse();
  const categories = items.map((sp) =>
    sp.scientific_name ? `${sp.common_name} (${sp.scientific_name})` : sp.common_name
  );
  return {
    chart: { type: "bar", height: Math.max(360, items.length * 40 + 80), backgroundColor: "#ffffff", style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: { categories, labels: { style: { fontSize: "11px", fontFamily: CHART_FONT } } },
    yAxis: { title: { text: "Número de indivíduos" }, allowDecimals: false },
    plotOptions: {
      bar: {
        color: "#2d7a2d",
        borderRadius: 3,
        dataLabels: {
          enabled: true,
          formatter() {
            return `${this.y} (${items[this.point.index].pct.toFixed(1)}%)`;
          }
        }
      }
    },
    series: [{ name: "Indivíduos", data: items.map((sp) => sp.count) }],
    legend: { enabled: false },
    credits: { enabled: false }
  };
}

export function buildFamilyChartOptions(stats) {
  const items = stats.top_families.slice().reverse();
  const categories = items.map((f) => f.family);
  return {
    chart: { type: "bar", height: Math.max(360, items.length * 40 + 80), backgroundColor: "#ffffff", style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: { categories, labels: { style: { fontSize: "11px", fontFamily: CHART_FONT } } },
    yAxis: { title: { text: "Percentual de indivíduos (%)" }, labels: { format: "{value}%" } },
    plotOptions: {
      bar: {
        color: "#3a8a3a",
        borderRadius: 3,
        dataLabels: {
          enabled: true,
          formatter() {
            return `${this.y.toFixed(1)}% (${items[this.point.index].count} ind.)`;
          }
        }
      }
    },
    series: [{ name: "% de indivíduos", data: items.map((f) => f.pct) }],
    legend: { enabled: false },
    credits: { enabled: false }
  };
}

// ── Highcharts option builders — 4.2.4 Origem das Espécies ───────────────────

export function buildOriginChartOptions(stats) {
  const slices = [
    { name: "Nativa", y: stats.native_count, color: ORIGIN_COLORS.Nativa },
    { name: "Exótica", y: stats.exotic_count, color: ORIGIN_COLORS["Exótica"] },
    { name: "Exótica Invasora", y: stats.invasive_count, color: ORIGIN_COLORS["Exótica Invasora"] }
  ];
  return {
    chart: { type: "pie", height: 380, backgroundColor: "#ffffff", style: { fontFamily: CHART_FONT } },
    title: { text: null },
    tooltip: {
      formatter() {
        return `<b>${this.point.name}</b><br>${this.y} indivíduos (${this.percentage.toFixed(1)}%)`;
      }
    },
    plotOptions: {
      pie: {
        innerSize: "50%",
        dataLabels: { enabled: true, format: "<b>{point.name}</b><br>{point.percentage:.1f}%" }
      }
    },
    series: [{ name: "Indivíduos", data: slices }],
    legend: { enabled: false },
    credits: { enabled: false }
  };
}

// ── Highcharts option builders — Índice GAAU ─────────────────────────────────

export function buildGaauGeneralChartOptions(stats) {
  const counts = GAAU_KEYS.map((k) => stats[k]);
  const pcts = GAAU_KEYS.map((k) => stats[k + "_pct"]);
  return {
    chart: { type: "column", height: 380, backgroundColor: "#ffffff", style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: { categories: GAAU_CATEGORIES, labels: { style: { fontSize: "12px", fontFamily: CHART_FONT } } },
    yAxis: { title: { text: "Número de indivíduos" }, allowDecimals: false },
    plotOptions: {
      column: {
        borderRadius: 3,
        colorByPoint: true,
        dataLabels: {
          enabled: true,
          formatter() {
            return `${this.y}<br>${pcts[this.point.index].toFixed(1)}%`;
          }
        }
      }
    },
    colors: GAAU_CATEGORIES.map((c) => GAAU_COLORS[c]),
    series: [{ name: "Indivíduos", data: counts }],
    legend: { enabled: false },
    credits: { enabled: false }
  };
}

export function buildGaauBairroChartOptions(stats) {
  const bairros = stats.by_bairro;
  return {
    chart: { type: "column", height: 420, backgroundColor: "#ffffff", style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: {
      categories: bairros.map((b) => b.name),
      labels: { style: { fontSize: "10px", fontFamily: CHART_FONT }, rotation: -35 }
    },
    yAxis: { title: { text: "Número de indivíduos" }, allowDecimals: false },
    plotOptions: { column: { borderRadius: 2 } },
    colors: GAAU_CATEGORIES.map((c) => GAAU_COLORS[c]),
    series: GAAU_CATEGORIES.map((cat, i) => ({
      name: cat,
      data: bairros.map((b) => b[GAAU_KEYS[i]])
    })),
    legend: { enabled: true },
    credits: { enabled: false }
  };
}

// ── Captura do SVG (mesmo padrão de ParticipatoryDiagnosisCharts.js) ──────────

function withViewBox(svg) {
  if (/\sviewBox=/.test(svg)) return svg;
  const wMatch = svg.match(/<svg[^>]*\swidth="(\d+(?:\.\d+)?)"/);
  const hMatch = svg.match(/<svg[^>]*\sheight="(\d+(?:\.\d+)?)"/);
  if (!wMatch || !hMatch) return svg;
  return svg.replace(/<svg\s/, `<svg viewBox="0 0 ${wMatch[1]} ${hMatch[1]}" `);
}

function renderSvg(options, width, height) {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  container.style.width = width + "px";
  container.style.height = height + "px";
  document.body.appendChild(container);
  try {
    const chart = Highcharts.chart(container, options);
    const svg = withViewBox(chart.getSVG());
    chart.destroy();
    return svg;
  } finally {
    document.body.removeChild(container);
  }
}

function figureHtml(svg, caption) {
  return "<figure>" + svg + (caption ? "<figcaption>" + caption + "</figcaption>" : "") + "</figure>";
}

function replaceAll(html, marker, replacement) {
  return html.split(marker).join(replacement);
}

/**
 * Resolve uma lista de marcadores [GRAFICO_*] presentes em `html`, buscando os dados uma
 * única vez (fetchStats) e renderizando cada gráfico via Highcharts + getSVG(). Nunca lança
 * exceção — em caso de falha, remove os marcadores não resolvidos. Retorna { html, failedMarkers }.
 */
async function resolveChartMarkers(html, fetchStats, specs) {
  const relevant = specs.filter((spec) => html && html.includes(spec.marker));
  if (relevant.length === 0) return { html, failedMarkers: [] };

  let result = html;
  const failedMarkers = [];

  try {
    const resp = await fetchStats();
    const stats = resp.data;

    relevant.forEach((spec) => {
      try {
        const svg = renderSvg(spec.buildOptions(stats), spec.width, spec.height);
        result = replaceAll(result, spec.marker, figureHtml(svg, spec.caption(stats)));
      } catch (e) {
        console.error("[GaauCharts] Falha ao renderizar " + spec.marker, e);
        result = replaceAll(result, spec.marker, "");
        failedMarkers.push(spec.marker);
      }
    });
  } catch (e) {
    console.error("[GaauCharts] Falha ao buscar estatísticas", e);
    relevant.forEach((spec) => {
      result = replaceAll(result, spec.marker, "");
      failedMarkers.push(spec.marker);
    });
  }

  if (failedMarkers.length > 0) {
    console.warn("[GaauCharts] Marcadores não resolvidos:", failedMarkers);
  }

  return { html: result, failedMarkers };
}

// ── Resolvers por capítulo ────────────────────────────────────────────────────

export function resolveDendrometricCharts(html, contaId) {
  return resolveChartMarkers(html, () => PmauService.GetDendrometricStats(contaId), [
    {
      marker: "[GRAFICO_DAP]",
      width: 720,
      height: 420,
      buildOptions: buildDapChartOptions,
      caption: (s) => `Figura 1 — Distribuição das classes diamétricas (DAP) por origem. Fonte: Inventário Municipal, ${s.year}.`
    },
    {
      marker: "[GRAFICO_ALTURA]",
      width: 520,
      height: 360,
      buildOptions: buildHeightChartOptions,
      caption: (s) => `Figura 2 — Distribuição dos indivíduos por classe de altura (porte arbóreo). Fonte: Inventário Municipal, ${s.year}.`
    }
  ]);
}

export function resolveSpeciesCharts(html, contaId) {
  return resolveChartMarkers(html, () => PmauService.GetSpeciesStats(contaId), [
    {
      marker: "[GRAFICO_ESPECIES]",
      width: 900,
      height: 420,
      buildOptions: buildSpeciesChartOptions,
      caption: (s) => `Gráfico 3 — Dez espécies de maior representatividade no inventário florestal urbano. Fonte: Inventário Municipal, ${s.year}.`
    },
    {
      marker: "[GRAFICO_FAMILIAS]",
      width: 900,
      height: 420,
      buildOptions: buildFamilyChartOptions,
      caption: (s) => `Gráfico 4 — Dez famílias botânicas com maior representatividade no inventário florestal urbano. Fonte: Inventário Municipal, ${s.year}.`
    }
  ]);
}

export function resolveOriginChart(html, contaId) {
  return resolveChartMarkers(html, () => PmauService.GetOriginStats(contaId), [
    {
      marker: "[GRAFICO_ORIGEM]",
      width: 580,
      height: 380,
      buildOptions: buildOriginChartOptions,
      caption: (s) => `Gráfico 5 — Distribuição percentual dos indivíduos arbóreos por origem. Fonte: Inventário Municipal, ${s.year}.`
    }
  ]);
}

export function resolveGaauIndexCharts(html, contaId) {
  return resolveChartMarkers(html, () => PmauService.GetGaauIndexStats(contaId), [
    {
      marker: "[GRAFICO_GAAU_GERAL]",
      width: 580,
      height: 380,
      buildOptions: buildGaauGeneralChartOptions,
      caption: (s) => `Figura 1 — Distribuição do Índice GAAU (Grau de Atenção para Árvores Urbanas). Fonte: Inventário Municipal, ${s.year}.`
    },
    {
      marker: "[GRAFICO_GAAU_BAIRRO]",
      width: 720,
      height: 420,
      buildOptions: buildGaauBairroChartOptions,
      caption: (s) => `Figura 2 — Distribuição do Índice GAAU por bairro (ordenado pelo número de indivíduos prioritários). Fonte: Inventário Municipal, ${s.year}.`
    }
  ]);
}
